/**
 * platform-billing-lark-page.tsx — Platform → Thông báo Lark: báo GIAO DỊCH của tổ chức vào nhóm Lark của đội vận hành
 * (đăng ký mới, mua / gia hạn gói, mua thêm, nạp credit AI). Route `/platform/lark`.
 * Backend mã hoá và chỉ trả URL dạng che, nên ô nhập luôn trống: để trống = giữ giá trị đã lưu.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Send } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Switch } from '@/components/ui/misc'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import {
  useBillingLark, useSaveBillingLark, useTestBillingLark,
  type BillingLarkEventKey, type BillingLarkSettings,
} from '@/hooks/use-platform-billing-lark'
import { LarkStatusDot } from './platform-feedback-lark-dialog'
import { changedEvents } from './billing-lark-helpers'

const SOURCE_NOTE: Record<BillingLarkSettings['source'], string> = {
  settings: 'Đang dùng cấu hình lưu tại đây.',
  env: 'Đang dùng cấu hình khai ở máy chủ (biến môi trường). Lưu ở đây sẽ thay thế nó.',
  none: 'Chưa cấu hình — giao dịch của tổ chức chưa được báo vào Lark.',
}

export function PlatformBillingLarkPage() {
  const { data, isLoading, isError } = useBillingLark()
  return (
    <div className="space-y-6">
      <PageHeader
        title="Thông báo Lark"
        description="Báo vào nhóm Lark của đội vận hành khi tổ chức đăng ký mới, mua / gia hạn gói, mua thêm hoặc nạp token AI. Tạo bot: Cài đặt nhóm → Bot → Thêm bot → Custom Bot, rồi dán Webhook URL vào đây."
      />
      {isLoading && <Loading label="Đang tải..." />}
      {isError && <ErrorState message="Không tải được cấu hình Lark." />}
      {data && <BillingLarkForm key={data.updatedAt ?? 'new'} settings={data} />}
    </div>
  )
}

function BillingLarkForm({ settings }: { settings: BillingLarkSettings }) {
  const save = useSaveBillingLark()
  const test = useTestBillingLark()
  const initialEnabled = settings.source === 'none' ? true : settings.enabled
  const [enabled, setEnabled] = useState(initialEnabled)
  const [url, setUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [clearSecret, setClearSecret] = useState(false)
  const [events, setEvents] = useState<Partial<Record<BillingLarkEventKey, boolean>>>(
    () => Object.fromEntries(settings.events.map((e) => [e.key, e.enabled])),
  )
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)

  const diff = changedEvents(settings.events, events)
  const needUrl = !url.trim() && !settings.hasUrl && settings.source !== 'env'
  const dirty = !!url.trim() || !!secret.trim() || clearSecret || enabled !== initialEnabled || Object.keys(diff).length > 0
  const canSave = dirty && !needUrl && !save.isPending

  const doSave = () =>
    save.mutate(
      { enabled, webhookUrl: url.trim() || undefined, secret: secret.trim() || undefined, clearSecret: clearSecret || undefined, events: Object.keys(diff).length ? diff : undefined },
      {
        onSuccess: () => { toast.success('Đã lưu cấu hình Lark'); setUrl(''); setSecret(''); setClearSecret(false); setResult(null) },
        onError: (e) => toast.error(platformApiError(e)),
      },
    )

  const doTest = (event: BillingLarkEventKey) => {
    setResult(null)
    test.mutate(
      { event, webhookUrl: url.trim() || undefined, secret: url.trim() ? secret.trim() : undefined },
      {
        onSuccess: (r) => setResult({ ok: r.ok, text: r.ok ? 'Đã gửi tin thử (có nhãn [TIN THỬ]) — kiểm tra nhóm Lark.' : r.error ?? 'Không gửi được' }),
        onError: (e) => setResult({ ok: false, text: platformApiError(e) }),
      },
    )
  }

  const doClear = () => {
    if (!window.confirm('Xoá cấu hình Lark đã lưu? Giao dịch sẽ không còn được báo (trừ khi máy chủ còn khai biến môi trường).')) return
    save.mutate({ clear: true }, {
      onSuccess: () => { toast.success('Đã xoá cấu hình Lark'); setEnabled(true) },
      onError: (e) => toast.error(platformApiError(e)),
    })
  }

  const canTest = !!url.trim() || settings.hasUrl || settings.envConfigured

  return (
    <div className="grid max-w-3xl gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LarkStatusDot active={settings.active} />
            {settings.active ? 'Đang báo vào Lark' : 'Chưa báo vào Lark'}
          </CardTitle>
          <CardDescription>{SOURCE_NOTE[settings.source]}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="bl-enabled" className="font-normal">Báo giao dịch vào Lark</Label>
            <Switch id="bl-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bl-url">Webhook URL của bot</Label>
            <Input
              id="bl-url" value={url} onChange={(e) => setUrl(e.target.value)} autoComplete="off" spellCheck={false}
              placeholder={settings.urlMasked ?? 'https://open.larksuite.com/open-apis/bot/v2/hook/…'}
            />
            <p className="text-xs text-muted-foreground">
              {settings.hasUrl ? 'Đã lưu (che): để trống để giữ URL hiện tại, nhập URL mới để thay.' : 'Phải là https và thuộc larksuite.com, feishu.cn hoặc larkoffice.com. URL chứa token — đừng chia sẻ. Có thể dùng chung nhóm với báo góp ý hoặc nhóm riêng.'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bl-secret">Khoá ký (nếu bot bật “Xác minh chữ ký”)</Label>
            <Input
              id="bl-secret" type="password" value={secret} onChange={(e) => { setSecret(e.target.value); if (e.target.value) setClearSecret(false) }}
              autoComplete="new-password" placeholder={settings.hasSecret ? '•••••••• (đã lưu — để trống để giữ)' : 'Không bắt buộc'}
            />
            {settings.hasSecret && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={clearSecret} onCheckedChange={(v) => { setClearSecret(v === true); if (v === true) setSecret('') }} />
                Xoá khoá ký đã lưu
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Báo những sự kiện nào</CardTitle>
          <CardDescription>Bật / tắt từng loại. “Gửi thử” gửi một thẻ mẫu (có nhãn [TIN THỬ]) của đúng loại đó.</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {settings.events.map((e) => (
            <div key={e.key} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <Label htmlFor={`bl-ev-${e.key}`} className="font-medium">{e.label}</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">{e.description}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => doTest(e.key)} disabled={test.isPending || !canTest}>
                  {test.isPending && test.variables?.event === e.key ? <Loader2 className="animate-spin" /> : <Send />}Gửi thử
                </Button>
                <Switch id={`bl-ev-${e.key}`} checked={events[e.key] ?? e.enabled} onCheckedChange={(v) => setEvents((cur) => ({ ...cur, [e.key]: v }))} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {result && (
        <p role="status" className={cn('rounded-md px-3 py-2 text-sm', result.ok ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300')}>
          {result.text}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <div>
          {settings.hasUrl && <Button type="button" variant="ghost" className="text-destructive" onClick={doClear} disabled={save.isPending}>Xoá cấu hình</Button>}
        </div>
        <Button type="button" onClick={doSave} disabled={!canSave}>
          {save.isPending && <Loader2 className="animate-spin" />}Lưu
        </Button>
      </div>
    </div>
  )
}
